import { computed, watch } from 'vue'
import { consola } from 'consola'
import { useBasketState } from '../useBasketState'
import {
  calculateMaxPointsToUse,
  validatePointsToUse,
  normalizePointsToUse,
  calculateCashback,
  type LoyaltyInfo,
} from '../../lib/loyaltyUtils'
import { useOrderPreview } from './useOrderPreview'
import { useLoyaltyStore } from '@entities/loyalty'
import { useInstitutionStore } from '@entities/institution'
import { useAuthState } from '~/entities/auth'

// Logger от Nuxt (consola)
const logger = consola.withTag('loyalty-calculation')

/**
 * ✅ ОТДЕЛЬНЫЙ COMPOSABLE для работы с программой лояльности
 *
 * Отвечает ТОЛЬКО за:
 * - Получение данных о баллах пользователя
 * - Расчет максимального количества баллов к списанию
 * - Расчет кешбэка
 * - Валидацию баллов
 */
export function useLoyaltyCalculation() {
  const basketState = useBasketState()
  const loyaltyStore = useLoyaltyStore()
  const institution = useInstitutionStore()
  const preview = useOrderPreview()
  const { isLoggedIn } = useAuthState()

  /**
   * Доля заказа, которую можно оплатить баллами, из настроек заведения.
   * Лимит у заведений разный (50%, 30%) — без него корзина считала бы по
   * захардкоженным 50% и предлагала списать больше, чем примет бэкенд.
   */
  const maxRedeemPercent = computed(
    () => institution.institution?.loyalty_max_percent_redeem ?? undefined,
  )

  /**
   * Информация о лояльности пользователя
   */
  const loyaltyInfo = computed((): LoyaltyInfo => {
    return {
      points: loyaltyStore.loyaltyInfo.points || 0,
      cashbackLevel: loyaltyStore.loyaltyInfo.cashbackLevel || '3%',
    }
  })

  /**
   * Баллы, которыми РЕАЛЬНО можно оплатить этот заказ.
   *
   * Это не весь баланс: баллы, уже захваченные неоплаченными заказами, потратить
   * второй раз нельзя (иначе одни и те же баллы уходили бы в несколько заказов).
   * Разницу знает только бэкенд — он считает `баланс − захваченные`.
   *
   * Корзина показывала полный баланс: у гостя с 1000 баллов и незакрытым заказом на 919
   * в блоке значилось «Доступно: 1000 баллов», а списать он мог 81. Источник истины —
   * preview; локальный баланс остаётся фолбэком, пока ответ не пришёл.
   */
  const availablePoints = computed(() => {
    const serverAvailable = preview.preview?.availablePoints
    return typeof serverAvailable === 'number' ? serverAvailable : loyaltyInfo.value.points
  })

  /** Баллы, недоступные к списанию, потому что их держат неоплаченные заказы. */
  const lockedPoints = computed(() => Math.max(0, loyaltyInfo.value.points - availablePoints.value))

  /**
   * Максимальное количество баллов которое можно списать
   * (50% от суммы корзины ПОСЛЕ промокода, но не больше доступных баллов)
   */
  const maxPoints = computed(() => {
    // Источник истины — preview-расчёт: бэкенд берёт процент от суммы товаров
    // ПЛЮС доставки, а локальная формула знает только товары и занижала максимум
    // (на заказе 500 ₽ + 300 ₽ доставки предлагала 250 вместо положенных 400).
    const serverMax = preview.preview?.maxPointsToUse
    if (typeof serverMax === 'number') {
      return Math.min(serverMax, availablePoints.value)
    }

    // Фолбэк, пока ответ не пришёл (или расчёт не делается — доставка без адреса).
    return calculateMaxPointsToUse(
      basketState.amountAfterPromocode,
      availablePoints.value,
      maxRedeemPercent.value,
    )
  })

  /**
   * Кешбэк который начислится за заказ
   *
   * ✅ База: amountAfterPoints — сумма товаров после промокода и списания баллов,
   *    БЕЗ доставки и сервисного сбора. Совпадает с логикой бэкенда (pointsToEarn).
   *
   * ✅ Реактивность: amountAfterPoints — computed в basketState, зависит напрямую
   *    от pointsToUse и useLoyaltyPoints. Обновление синхронное, без async watcher.
   *
   * ❌ Было: basketState.finalAmount — plain ref, обновляется через async watcher
   *    в usePriceCalculation. Это приводило к зависанию значения при вводе баллов
   *    и к несоответствию с бэкендом (фронт включал доставку + сервисный сбор в базу).
   */
  const cashback = computed(() => {
    return calculateCashback(
      basketState.amountAfterPoints,
      loyaltyInfo.value.cashbackLevel,
    )
  })

  /**
   * Валидны ли текущие баллы к списанию
   */
  const isPointsValid = computed(() => {
    if (!basketState.useLoyaltyPoints) return true

    return validatePointsToUse(
      basketState.pointsToUse,
      basketState.amountAfterPromocode,
      availablePoints.value,
      maxRedeemPercent.value,
    )
  })

  /**
   * Форматированная информация о лояльности
   */
  const formattedInfo = computed(() => {
    let cashbackRate: number
    const level = loyaltyInfo.value.cashbackLevel

    if (typeof level === 'string') {
      const cleanValue = level.replace('%', '').trim()
      cashbackRate = Number(cleanValue) / 100
    } else {
      cashbackRate = Number(level)
    }

    return {
      availablePoints: availablePoints.value,
      /** Весь баланс — для подсказки «часть баллов держат неоплаченные заказы». */
      totalPoints: loyaltyInfo.value.points,
      lockedPoints: lockedPoints.value,
      maxPointsToUse: maxPoints.value,
      cashbackRate,
      cashbackAmount: cashback.value,
      pointsToUse: basketState.pointsToUse,
      isPointsUsageValid: isPointsValid.value,
    }
  })

  // ========================================
  // ACTIONS
  // ========================================

  /**
   * Обработать ввод баллов (нормализовать)
   */
  function handlePointsInput(value: number): void {
    const normalized = normalizePointsToUse(
      value,
      basketState.amountAfterPromocode,
      availablePoints.value,
      maxRedeemPercent.value,
    )

    basketState.setPointsToUse(normalized)
  }

  /**
   * Установить максимальное количество баллов
   */
  function setMaxPoints(): void {
    basketState.setMaxPoints()
  }

  /**
   * Загрузить информацию о лояльности
   */
  async function loadLoyaltyInfo(): Promise<void> {
    if (!isLoggedIn.value) return

    try {
      await loyaltyStore.fetchLoyaltyInfo()
    } catch (error) {
      logger.error('Failed to load loyalty info', error)
    }
  }

  // ========================================
  // SIDE EFFECTS: Обновляем basketState
  // ========================================

  /**
   * Синхронизируем информацию о лояльности с basketState.
   *
   * Явные зависимости в source:
   * - cartTotal, promocodeDiscount → влияют на maxPoints
   * - pointsToUse, useLoyaltyPoints → влияют на amountAfterPoints → на cashback
   *   (ранее этих зависимостей не было, поэтому watcher не перезапускался при вводе баллов)
   */
  watch(
    () => ({
      availablePoints: availablePoints.value,
      maxPointsToUse: maxPoints.value,
      cashbackAmount: cashback.value,
      cashbackLevel: loyaltyInfo.value.cashbackLevel,
      // Зависимости для maxPoints
      cartTotal: basketState.cartTotal,
      promocodeDiscount: basketState.promocodeDiscount,
      // ✅ Явные зависимости для cashback (amountAfterPoints = cartTotal - promo - points)
      pointsToUse: basketState.pointsToUse,
      useLoyaltyPoints: basketState.useLoyaltyPoints,
    }),
    (info) => {
      basketState.updateLoyaltyInfo({
        availablePoints: info.availablePoints,
        maxPointsToUse: info.maxPointsToUse,
        cashbackAmount: info.cashbackAmount,
        cashbackLevel: String(info.cashbackLevel),
      })
    },
    { immediate: true, deep: true },
  )

  return {
    // Computed
    loyaltyInfo,
    maxPoints,
    cashback,
    isPointsValid,
    formattedInfo,

    // Actions
    handlePointsInput,
    setMaxPoints,
    loadLoyaltyInfo,
  }
}


